import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { LIST_PAGE_SIZE_OPTIONS } from '../../constants';

export interface GridPaginationBarProps {
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /** Prefix for stable input ids (e.g. `requests` / `po`). */
  idPrefix: string;
}

export function GridPaginationBar({
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  idPrefix
}: GridPaginationBarProps): ReactElement {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const sizeOptions = useMemo((): number[] => {
    const base: number[] = [...LIST_PAGE_SIZE_OPTIONS];
    if (!base.some((x) => x === pageSize)) {
      base.push(pageSize);
      base.sort((a, b) => a - b);
    }
    return base;
  }, [pageSize]);

  const [pageInput, setPageInput] = useState(String(page));

  useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  function commitPage(): void {
    const raw = pageInput.trim();
    if (raw === '') {
      setPageInput(String(page));
      return;
    }
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) {
      setPageInput(String(page));
      return;
    }
    const clamped = Math.min(Math.max(1, n), totalPages);
    if (clamped !== page) {
      onPageChange(clamped);
    }
    setPageInput(String(clamped));
  }

  return (
    <div className="pagination-bar" role="navigation" aria-label="Table pagination">
      <span className="pagination-meta">
        Page {page} of {totalPages} ({totalCount} total)
      </span>
      <div className="pagination-controls">
        <div className="pagination-field">
          <label htmlFor={`${idPrefix}-page-size`} className="pagination-label">
            Rows per page
          </label>
          <select
            id={`${idPrefix}-page-size`}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {sizeOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="pagination-field pagination-field--page">
          <label htmlFor={`${idPrefix}-page-num`} className="pagination-label">
            Go to page
          </label>
          <input
            id={`${idPrefix}-page-num`}
            type="text"
            inputMode="numeric"
            className="pagination-page-input"
            autoComplete="off"
            aria-label="Page number"
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value.replace(/\D/g, ''))}
            onBlur={commitPage}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitPage();
                (e.target as HTMLInputElement).blur();
              }
            }}
          />
        </div>
        <div className="pagination-nav">
          <button
            type="button"
            className="secondary pagination-nav-btn"
            disabled={!canPrev}
            onClick={() => onPageChange(page - 1)}
            aria-label="Previous page"
          >
            Previous
          </button>
          <button
            type="button"
            className="secondary pagination-nav-btn"
            disabled={!canNext}
            onClick={() => onPageChange(page + 1)}
            aria-label="Next page"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
